// src/pages/projects/container/ProjectHeader.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Typography, Button, IconButton, TextField, 
  AvatarGroup, Avatar, Tooltip, useTheme
} from '@mui/material';
import {
  FolderOutlined as FolderIcon,
  StarBorder as StarOutlineIcon,
  Star as StarIcon,
  MoreHoriz as MoreIcon,
  PersonAdd as PersonAddIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material';
import { format } from 'date-fns';

// Import ViewSelector component
import ViewSelector from '../../../components/projects/ViewSelector';

const ProjectHeader = ({ 
  project, 
  isEditingTitle, 
  titleValue, 
  titleInputRef, 
  isStarred, 
  canEdit,
  onOpenMenu,
  onStartEditingTitle,
  onTitleChange,
  onTitleSubmit,
  onToggleStar
}) => {
  const theme = useTheme();
  
  // Helper functions
  const formatDate = (date) => {
    if (!date) return 'Not set';
    try {
      return format(new Date(date), 'MMM d, yyyy');
    } catch (error) {
      return 'Invalid date';
    }
  };
  
  // Get status color and label
  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return theme.palette.grey[500];
      case 'planning': return theme.palette.info.main;
      case 'active': return theme.palette.success.main;
      case 'on_hold': return theme.palette.warning.main;
      case 'completed': return theme.palette.success.dark;
      case 'cancelled': return theme.palette.error.main;
      default: return theme.palette.grey[500];
    }
  };
  
  const getStatusLabel = (status) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'planning': return 'Planning';
      case 'active': return 'Active';
      case 'on_hold': return 'On Hold';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return 'Draft';
    }
  };
  
  return (
    <Box sx={{ 
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
      pb: 0
    }}>
      {/* Top Navigation Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 1.5
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Project Icon & Title */}
          <FolderIcon 
            sx={{ 
              mr: 1.5, 
              fontSize: 24
            }} 
          />
          
          {isEditingTitle ? (
            <TextField
              inputRef={titleInputRef}
              value={titleValue}
              onChange={onTitleChange}
              onKeyDown={onTitleSubmit}
              onBlur={onTitleSubmit}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: { 
                  fontSize: '1.25rem', 
                  fontWeight: 600,
                  '&:focus': {
                    boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`
                  }
                }
              }}
              sx={{ 
                minWidth: 200,
                mr: 1
              }}
            />
          ) : (
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                cursor: canEdit ? 'pointer' : 'default',
                mr: 1,
                '&:hover': canEdit ? {
                  color: theme.palette.primary.main
                } : {}
              }}
              onClick={canEdit ? onStartEditingTitle : undefined}
            >
              {project.name}
            </Typography>
          )}
          
          {/* Star button */}
          <IconButton 
            size="small" 
            onClick={onToggleStar}
            sx={{ mr: 1 }}
          >
            {isStarred ? (
              <StarIcon sx={{ color: theme.palette.warning.main }} />
            ) : (
              <StarOutlineIcon />
            )}
          </IconButton>
          
          {/* Status badge */}
          <Box
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              py: 0.5,
              px: 1.5,
              borderRadius: 4,
              backgroundColor: alpha(getStatusColor(project.status), 0.1),
              color: getStatusColor(project.status),
              fontSize: '0.8125rem',
              fontWeight: 500,
              cursor: canEdit ? 'pointer' : 'default',
              '&:hover': canEdit ? {
                backgroundColor: alpha(getStatusColor(project.status), 0.2)
              } : {}
            }}
            onClick={canEdit ? onOpenMenu : undefined}
          >
            {getStatusLabel(project.status)}
          </Box>
          
          {/* Brand information */}
          {project.brandId && (
            <Tooltip title={`Brand: ${project.brandId.name || 'Unknown Brand'}`}>
              <Box
                component={Link}
                to={`/brands/${project.brandId._id || project.brandId}`}
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  ml: 1,
                  py: 0.5,
                  px: 1.5,
                  borderRadius: 4,
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.text.primary,
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2)
                  }
                }}
              >
                <BusinessIcon sx={{ mr: 1, fontSize: '1rem' }} />
                {typeof project.brandId === 'object' ? project.brandId.name : 'Brand'}
              </Box>
            </Tooltip>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Project metadata */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            mr: 2
          }}>
            {/* Due date */}
            {project.dates?.plannedEndDate && (
              <Tooltip title="Due date">
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  mr: 2,
                  color: theme.palette.text.secondary
                }}>
                  <CalendarIcon fontSize="small" sx={{ mr: 0.5 }} />
                  <Typography variant="body2">
                    {formatDate(project.dates.plannedEndDate)}
                  </Typography>
                </Box>
              </Tooltip>
            )}
            
            {/* Team members */}
            {project.team?.length > 0 && (
              <AvatarGroup 
                max={4}
                sx={{ 
                  '& .MuiAvatar-root': { 
                    width: 28, 
                    height: 28, 
                    fontSize: '0.8rem',
                    borderColor: theme.palette.background.paper
                  }
                }}
              >
                {project.team.map(member => (
                  <Tooltip 
                    key={member.user?._id} 
                    title={`${member.user?.firstName} ${member.user?.lastName}`}
                  >
                    <Avatar 
                      src={member.user?.photo} 
                      alt={member.user?.firstName}
                    >
                      {member.user?.firstName?.[0]}
                    </Avatar>
                  </Tooltip>
                ))}
              </AvatarGroup>
            )}
          </Box>
          
          {/* Action buttons */}
          {canEdit && (
            <>
              <Button
                startIcon={<PersonAddIcon />}
                variant="outlined"
                size="small"
                sx={{ 
                  borderRadius: 4,
                  textTransform: 'none'
                }}
                component={Link}
                to={`/projects/${project._id}/team`}
              >
                Share
              </Button>
              
              <IconButton 
                onClick={onOpenMenu}
                size="small"
                sx={{ 
                  ml: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2
                }}
              >
                <MoreIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
      
      {/* View Selector */}
      <ViewSelector projectId={project._id} />
    </Box>
  );
};

export default ProjectHeader;